/**
 * Partage d'un planning — liste des personnes ayant accès à un projet avec leur droit
 * **effectif** et sa provenance (héritée de l'équipe ou spécifique au projet), un sélecteur
 * de rôle, une case `canDelete` **indépendante du rôle**, et un formulaire d'invitation.
 *
 * À la création, le lien d'invitation est affiché **une seule fois** : le jeton en clair
 * n'est pas stocké côté serveur (§10 de la spec), donc `invitationUrl` ne vit que dans l'état
 * local de ce composant. Fermer le dialogue — ou simplement recharger la liste — le perd
 * définitivement, exactement comme le serveur le veut.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import { fetchAllPages } from '../lib/pagination.js';
import { useAsync } from '../hooks/useAsync.js';
import { appAlert } from './service.js';
import { messageModeleOrganisation } from '../lib/org-errors.js';
import { Empty, LoadError, TruncatedListWarning } from '../components/common.jsx';
import { RoleBadge } from '../components/RoleBadge.jsx';
import { Modal } from '../components/Modal.jsx';
import {
  BTN_GHOST_SM,
  BTN_PRIMARY,
  CHECK,
  CHOIX_LABEL,
  FORM_FIELD,
  FORM_LABEL,
  FORM_ROW,
  INPUT,
  SELECT,
  SELECT_SM,
  TAG,
  TD,
  TH,
} from '../ui/theme.js';

/** `ProjectMember` n'a pas de rôle `owner` (notion d'équipe) — cf. `schemas.js` serveur. */
const PROJECT_MEMBER_ROLES = ['viewer', 'member', 'admin'];

/** Mêmes clés que `RoleBadge::ROLE_LABEL_KEYS` — un `<option>` ne peut pas accueillir le composant. */
const ROLE_OPTION_LABEL_KEYS = {
  admin: 'roleBadge.admin',
  member: 'roleBadge.member',
  viewer: 'roleBadge.viewer',
};

/** Littéraux — le gate `keys.test.js` ne voit pas une clé de gabarit calculée. */
const SOURCE_LABEL_KEYS = {
  team: 'shareProject.source.team',
  project: 'shareProject.source.project',
};

export function ShareProjectDialog({ projectId, canManage = false, onClose }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [canDelete, setCanDelete] = useState(false);
  const [invitationUrl, setInvitationUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [addedEmail, setAddedEmail] = useState(null);

  const {
    data: membersData,
    error: membersError,
    loading: membersLoading,
    reload,
  } = useAsync(
    (signal) => fetchAllPages(`/projects/${projectId}/members`, { signal }),
    [projectId],
  );
  const members = membersData?.data ?? [];

  const changeRole = async (member, nextRole) => {
    setBusy(true);
    try {
      await api.patch(`/projects/${projectId}/members/${member.userId}`, {
        role: nextRole,
        canDelete: member.canDelete,
      });
      await reload();
    } catch (error) {
      await appAlert(t('shareProject.roleChangeFailed', { message: error.message }), {
        title: t('shareProject.title'),
      });
      await reload();
    } finally {
      setBusy(false);
    }
  };

  /** Le droit de supprimer se règle SANS toucher au rôle — c'est le point de l'exigence #3. */
  const toggleCanDelete = async (member) => {
    setBusy(true);
    try {
      await api.patch(`/projects/${projectId}/members/${member.userId}`, {
        role: member.role,
        canDelete: !member.canDelete,
      });
      await reload();
    } catch (error) {
      await appAlert(t('shareProject.canDeleteChangeFailed', { message: error.message }), {
        title: t('shareProject.title'),
      });
    } finally {
      setBusy(false);
    }
  };

  const sendInvite = async (event) => {
    event.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    setCopied(false);
    setAddedEmail(null);
    try {
      const { data } = await api.post(`/projects/${projectId}/invitations`, {
        email: email.trim(),
        role,
        canDelete,
      });
      if (data.added) {
        // Membre déjà actif de l'organisation : ajouté sans invitation, donc sans lien.
        setInvitationUrl(null);
        setAddedEmail(email.trim());
        await reload();
      } else {
        setAddedEmail(null);
        setInvitationUrl(data.invitationUrl);
      }
      setEmail('');
      setRole('member');
      setCanDelete(false);
    } catch (error) {
      await appAlert(
        messageModeleOrganisation(error, t) ??
          t('shareProject.inviteFailed', { message: error.message }),
        { title: t('shareProject.title') },
      );
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(invitationUrl);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Modal title={t('shareProject.title')} onClose={onClose}>
      <div className="flex flex-col gap-4">
        {/* `membersLoading` garde l'état vide : à l'ouverture de la boîte, `members` vaut `[]`
            le temps de la réponse et l'écran annonçait « aucun membre » à un projet partagé.
            Même garde que ProjectsPage, TeamsPage et ListView. */}
        {membersError && <LoadError error={membersError} onRetry={reload} />}
        {membersData?.truncated && <TruncatedListWarning />}
        {membersLoading ? (
          <p className="text-sm text-ink-soft">{t('shareProject.loading')}</p>
        ) : membersError ? null : members.length === 0 ? (
          <Empty>{t('shareProject.empty')}</Empty>
        ) : (
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className={TH}>{t('shareProject.columnPerson')}</th>
                <th className={TH}>{t('shareProject.columnRole')}</th>
                <th className={TH}>{t('shareProject.columnSource')}</th>
                <th className={TH}>{t('shareProject.columnCanDelete')}</th>
              </tr>
            </thead>
            <tbody>
              {members.map((member) => (
                <tr key={member.userId}>
                  <td className={TD}>
                    <span className="block text-ink">{member.name || member.email}</span>
                    {member.name && member.email && (
                      <span className="block font-mono text-[11px] text-ink-faint">
                        {member.email}
                      </span>
                    )}
                  </td>
                  <td className={TD}>
                    {/*
                     * `ProjectMember.role` n'a pas de valeur `owner` — la propriété est une
                     * notion d'équipe — mais `listProjectMembers` peut rendre `role: 'owner'`
                     * comme droit EFFECTIF pour l'owner d'équipe. `PROJECT_MEMBER_ROLES` ne
                     * l'inclut pas : lui présenter le `<select>` l'affichait en « viewer » et
                     * toute modification renvoyait `role: 'owner'`, rejeté par
                     * `projectMemberUpdateSchema`. La ligne owner reste donc en lecture seule,
                     * comme un non-gestionnaire.
                     */}
                    {canManage && member.role !== 'owner' ? (
                      <select
                        aria-label={t('shareProject.roleAria', {
                          name: member.name || member.email,
                        })}
                        className={SELECT_SM}
                        value={member.role}
                        onChange={(event) => changeRole(member, event.target.value)}
                        disabled={busy}
                      >
                        {PROJECT_MEMBER_ROLES.map((r) => (
                          <option key={r} value={r}>
                            {t(ROLE_OPTION_LABEL_KEYS[r])}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <RoleBadge role={member.role} />
                    )}
                  </td>
                  <td className={TD}>
                    <span className={`${TAG} border-line text-ink-soft`}>
                      {t(SOURCE_LABEL_KEYS[member.source] || 'shareProject.source.project')}
                    </span>
                  </td>
                  <td className={TD}>
                    <label className={CHOIX_LABEL}>
                      <input
                        className={CHECK}
                        type="checkbox"
                        aria-label={t('shareProject.canDeleteAria', {
                          name: member.name || member.email,
                        })}
                        checked={member.canDelete}
                        // Même raison que le sélecteur de rôle : `role: 'owner'` dans le PATCH
                        // échouerait la validation ; l'owner a de toute façon `canDelete: true`
                        // de droit (§ resolveAccess), la case n'a rien à faire ici.
                        disabled={!canManage || member.role === 'owner' || busy}
                        onChange={() => toggleCanDelete(member)}
                      />
                      {t('shareProject.canDelete')}
                    </label>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {canManage && (
          <form onSubmit={sendInvite} className="flex flex-col gap-2 border-t-2 border-line pt-4">
            <label className={FORM_FIELD}>
              <span className={FORM_LABEL}>{t('shareProject.emailLabel')}</span>
              <input
                type="email"
                className={INPUT}
                placeholder={t('shareProject.emailPlaceholder')}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={busy}
              />
            </label>

            {/* Ligne de formulaire MD (reference.md §6.0), comme le champ e-mail au-dessus. La
                case prend la hauteur des contrôles (h-8) pour se centrer sur eux. */}
            <div className={FORM_ROW}>
              <label className={FORM_FIELD}>
                <span className={FORM_LABEL}>{t('shareProject.roleLabel')}</span>
                <select
                  className={SELECT}
                  value={role}
                  onChange={(event) => setRole(event.target.value)}
                  disabled={busy}
                >
                  {PROJECT_MEMBER_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {t(ROLE_OPTION_LABEL_KEYS[r])}
                    </option>
                  ))}
                </select>
              </label>

              <label className={`${CHOIX_LABEL} h-8`}>
                <input
                  className={CHECK}
                  type="checkbox"
                  checked={canDelete}
                  disabled={busy}
                  onChange={(event) => setCanDelete(event.target.checked)}
                />
                {t('shareProject.canDeleteNewInvite')}
              </label>

              <button type="submit" className={BTN_PRIMARY} disabled={busy}>
                {t('shareProject.invite')}
              </button>
            </div>
          </form>
        )}

        {addedEmail && (
          <p
            role="status"
            className="border-2 border-accent-trait bg-accent-wash p-3 text-sm text-accent-ink"
          >
            {t('shareProject.addedDirectly', { email: addedEmail })}
          </p>
        )}

        {invitationUrl && (
          <div className="border-2 border-accent-trait bg-accent-wash p-3">
            <p role="alert" className="text-sm font-semibold text-accent-ink">
              {t('shareProject.invitationLinkWarning')}
            </p>
            <p className="mt-2 break-all font-mono text-[11px] text-ink">{invitationUrl}</p>
            <button type="button" className={`${BTN_GHOST_SM} mt-2`} onClick={copyLink}>
              {t('shareProject.copyLink')}
            </button>
            {copied && (
              <span className="ml-2 text-xs text-ink-soft">{t('shareProject.linkCopied')}</span>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
